import { parsePhoneNumberWithError, isValidPhoneNumber, CountryCode } from 'libphonenumber-js';

function stripLegacyMexicanMobilePrefix(phone: string): string {
  const digits = phone.replace(/[^\d+]/g, '');
  if (digits.startsWith('+521') && digits.length === 14) {
    return `+52${digits.slice(4)}`;
  }
  return phone;
}

export interface PhoneValidationResult {
  isValid: boolean;
  countryCode?: string;
  nationalNumber?: string;
  formatInternational?: string;
  formatNational?: string;
  errorMessage?: string;
}

export function validatePhoneNumber(phone: string, defaultCountryCode: CountryCode = 'AR'): PhoneValidationResult {
  try {
    // Si no hay número, es inválido
    if (!phone || phone.trim() === '') {
      return {
        isValid: false,
        errorMessage: 'El número de teléfono es requerido'
      };
    }

    const normalizedPhone = stripLegacyMexicanMobilePrefix(phone);

    // Validar formato básico
    if (!isValidPhoneNumber(normalizedPhone)) {
      return {
        isValid: false,
        errorMessage: 'Formato de teléfono no válido'
      };
    }

    // Parsear el número para obtener información detallada
    const parsedPhone = parsePhoneNumberWithError(normalizedPhone, defaultCountryCode);
    
    if (!parsedPhone.isValid()) {
      return {
        isValid: false,
        errorMessage: 'El número de teléfono no es válido para el país seleccionado'
      };
    }

    // Número válido, retornar información
    return {
      isValid: true,
      countryCode: parsedPhone.country,
      nationalNumber: parsedPhone.nationalNumber,
      formatInternational: parsedPhone.formatInternational(),
      formatNational: parsedPhone.formatNational()
    };

  } catch (error) {
    return {
      isValid: false,
      errorMessage: 'Error al validar el número de teléfono'
    };
  }
}

export function createPhoneValidationTest(
  defaultCountryCode: CountryCode = 'AR',
  allowEmpty: boolean = false,
  customErrorMessage?: string
) {
  return function(this: any, value: string | null | undefined) {
    if (!value && allowEmpty) return true;
    
    if (!value && !allowEmpty) {
      return this.createError({
        message: customErrorMessage || 'El número de teléfono es requerido'
      });
    }
    
    const validation = validatePhoneNumber(value as string, defaultCountryCode);
    if (!validation.isValid) {
      return this.createError({
        message: customErrorMessage || validation.errorMessage || 'Número de teléfono inválido'
      });
    }
    
    return true;
  };
}

export function getPhoneErrorMessage(phone: string, defaultCountryCode: CountryCode = 'AR'): string {
  const validation = validatePhoneNumber(phone, defaultCountryCode);
  return validation.errorMessage || '';
}

export function formatPhoneNumber(phone: string, defaultCountryCode: CountryCode = 'AR'): string {
  try {
    const parsedPhone = parsePhoneNumberWithError(stripLegacyMexicanMobilePrefix(phone), defaultCountryCode);
    if (parsedPhone.isValid()) {
      return parsedPhone.formatInternational();
    }
    return phone;
  } catch (error) {
    return phone;
  }
}
